import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { ChevronDown, ChevronUp, Loader, X } from 'react-feather';
import { BRAND_COLORS } from '../globalTheme';
import { getSelectSearchStyles } from './styleUtils';
export interface Option {
	value: string;
	label: string;
}

export interface SelectSearchProps {
	options: Option[];
	placeholder?: string;
	label?: string;
	onChange: (selectedOption: Option | null) => void;
	initialValue?: Option | null;
	isInitiallyOpen?: boolean;
	disabled?: boolean;
	loading?: boolean;
	noOptionsText?: string;
	initialSearchTerm?: string;
}

const SelectSearch: React.FC<SelectSearchProps> = ({
	options,
	placeholder = 'Buscar...',
	label = '',
	onChange,
	initialValue = null,
	isInitiallyOpen = false,
	disabled = false,
	loading = false,
	noOptionsText = 'No se encontraron opciones...',
	initialSearchTerm = ''
}) => {
	const styles = getSelectSearchStyles();
	const [isOpen, setIsOpen] = useState(isInitiallyOpen || !!initialSearchTerm);
	const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
	const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
	const [selectedOption, setSelectedOption] = useState<Option | null>(initialValue);
	const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	function findOptionCoincidences(
		userInput: string,
		options: Option[],
		key: keyof Option = 'label'
	) {
		if (!userInput || !Array.isArray(options)) {
			return [];
		}

		const searchTerms = userInput
			.toLowerCase()
			.split(/\s+/)
			.filter((term) => term.trim().length > 0);

		if (searchTerms.length === 0) {
			return [];
		}

		const regexPatterns = searchTerms.map((term) => new RegExp(term, 'i'));

		const matches = options.filter((option) => {
			const templateName = (option[key] as string) || '';
			return regexPatterns.every((regex) => regex.test(templateName));
		});
		return matches;
	}

	useEffect(() => {
		if (searchTerm === '') {
			setFilteredOptions(options);
		} else {
			const filtered = findOptionCoincidences(searchTerm, Object.values(options));
			setFilteredOptions(filtered);
		}
		setHighlightedIndex(-1);
	}, [searchTerm, options]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
		setIsOpen(true);
	};

	const handleOptionClick = (option: Option) => {
		setSelectedOption(option);
		setSearchTerm('');
		setIsOpen(false);
		onChange(option);
	};

	const clearSelection = () => {
		setSelectedOption(null);
		setSearchTerm('');
		onChange(null);
		inputRef.current?.focus();
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setHighlightedIndex((prevIndex) =>
				prevIndex < filteredOptions.length - 1 ? prevIndex + 1 : prevIndex
			);
			setIsOpen(true);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setHighlightedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0));
		} else if (e.key === 'Enter' && highlightedIndex >= 0) {
			e.preventDefault();
			handleOptionClick(filteredOptions[highlightedIndex]);
		} else if (e.key === 'Escape') {
			e.preventDefault();
			setIsOpen(false);
		}
	};

	const toggleDropdown = () => {
		if (!disabled) {
			setIsOpen(!isOpen);
			if (!isOpen) {
				inputRef.current?.focus();
			}
		}
	};

	return (
		<div className={`relative w-full`} ref={dropdownRef}>
			<label style={styles.inputLabel}>{label}</label>
			<div style={styles.selectSearchContainerCss} className="w-full">
				<input
					ref={inputRef}
					type="text"
					placeholder={selectedOption ? selectedOption.label : placeholder}
					value={selectedOption ? selectedOption.label : searchTerm}
					onChange={handleInputChange}
					onFocus={() => setIsOpen(true)}
					onKeyDown={handleKeyDown}
					className={`w-full ${disabled || loading ? 'bg-gray-100 cursor-not-allowed' : 'bg-tranpa'}`}
					disabled={disabled || loading}
				/>
				<div className="absolute inset-y-0 right-0 flex items-center gap-3 mr-2">
					{loading && (
						<Loader
							color={BRAND_COLORS.brand600}
							size={24}
							height={24}
							data-testid="loader"
							className="spin-animation"
						/>
					)}
					{!loading && selectedOption && (
						<X
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								clearSelection();
							}}
							color={BRAND_COLORS.brand600}
							className="hover:text-black focus:outline-none"
							aria-label="Clear selection"
						>
							×
						</X>
					)}
					{!loading &&
						(isOpen ? (
							<ChevronUp onClick={toggleDropdown} className="cursor-pointer" />
						) : (
							<ChevronDown onClick={toggleDropdown} className="cursor-pointer" />
						))}
				</div>
			</div>

			{isOpen && (
				<div
					className="absolute w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto custom-scrollbar"
					style={{ zIndex: 1001 }}
				>
					{filteredOptions.length > 0 ? (
						<ul className="py-1">
							{filteredOptions.map((option, index) => (
								<li
									key={option.value}
									onClick={() => handleOptionClick(option)}
									className={`px-3 py-2 cursor-pointer ${
										index === highlightedIndex ? 'bg-blue-100' : 'hover:bg-gray-100'
									}`}
								>
									{option.label}
								</li>
							))}
						</ul>
					) : (
						<div className="px-3 py-2 text-gray-500">{noOptionsText}</div>
					)}
				</div>
			)}
		</div>
	);
};

export default SelectSearch;
